import type { Editor } from "@tiptap/react";
import type { EditorProps } from "@tiptap/pm/view";

/**
 * Image insertion for the day-note editor: pasted/dropped image files become
 * inline base64 <img> nodes. Photos are downscaled (and re-encoded as JPEG) so
 * the serialized HTML stays comfortably inside the API's notes cap and the
 * localStorage draft mirror (ADR-0011).
 */

const MAX_DIM = 1600;
const JPEG_QUALITY = 0.85;

function isImageFile(file: File | undefined | null): file is File {
  return Boolean(file && file.type.startsWith("image/"));
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("image read failed"));
    reader.readAsDataURL(file);
  });
}

function downscale(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) {
        resolve(dataUrl);
        return;
      }
      const scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
      if (scale >= 1) {
        resolve(dataUrl);
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
    };
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = dataUrl;
  });
}

export function insertImageFromFile(editor: Editor, file: File): void {
  if (!isImageFile(file)) return;
  fileToDataUrl(file)
    .then(downscale)
    .then((src) => editor.chain().focus().setImage({ src }).run())
    .catch((err: unknown) => {
      // A bad/oversized image must not take the editor down; make it visible.
      console.error("note: image insert failed", err);
    });
}

/** editorProps: Ctrl/Cmd+K opens the link popover; pasted/dropped images insert. */
export function noteEditorProps(getEditor: () => Editor | null, onModK: () => void): EditorProps {
  return {
    handleKeyDown: (_view, event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        onModK();
        return true;
      }
      return false;
    },
    handlePaste: (_view, event) => {
      const file = Array.from(event.clipboardData?.files ?? []).find(isImageFile);
      if (!file) return false;
      event.preventDefault();
      const editor = getEditor();
      if (editor) insertImageFromFile(editor, file);
      return true;
    },
    handleDrop: (_view, event) => {
      const file = Array.from(event.dataTransfer?.files ?? []).find(isImageFile);
      if (!file) return false;
      event.preventDefault();
      const editor = getEditor();
      if (editor) insertImageFromFile(editor, file);
      return true;
    },
  };
}
